/**
 * Shared hand-drawn inline icon set (stroke="currentColor", so the icon-color
 * editor works by setting the wrapper's CSS `color`) — used by every
 * editable page (service pages, About page) so admins pick from one
 * consistent list everywhere. Originally lived only in TemplateServicePage.jsx;
 * extracted here so it's not duplicated per page.
 */
export const ICON_NAMES = [
  'chevronRight', 'arrowRight', 'arrowUpRight', 'calendar', 'clock', 'bolt', 'users',
  'shield', 'shieldCheck', 'layers', 'wrench', 'window', 'plug', 'cloud', 'code', 'check', 'plus',
  'globe', 'chip', 'image', 'lock', 'file', 'message', 'star', 'target',
  'bot', 'brain', 'lightbulb', 'workflow', 'search', 'compass', 'refresh',
];

export default function Icon({ name, size = 20, color }) {
  const common = {
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.8,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    'aria-hidden': true,
    ...(color ? { style: { color } } : null),
  };
  switch (name) {
    case 'chevronRight':
      return <svg {...common} strokeWidth={2}><path d="M9 6l6 6-6 6" /></svg>;
    case 'arrowRight':
      return <svg {...common}><path d="M5 12h14M13 6l6 6-6 6" /></svg>;
    case 'arrowUpRight':
      return <svg {...common}><path d="M7 17L17 7M8 7h9v9" /></svg>;
    case 'calendar':
      return <svg {...common}><rect x="4" y="5" width="16" height="15" rx="2" /><path d="M8 3v4M16 3v4M4 10h16" /></svg>;
    case 'clock':
      return <svg {...common}><circle cx="12" cy="12" r="8.5" /><path d="M12 7.5V12l3 2" /></svg>;
    case 'bolt':
      return <svg {...common}><path d="M12.5 3L5 14h5.5L11 21l7.5-11H13z" strokeLinejoin="round" /></svg>;
    case 'users':
      return (
        <svg {...common}>
          <circle cx="9" cy="8.5" r="3" />
          <path d="M3.5 19c1-3 3.3-4.5 5.5-4.5s4.5 1.5 5.5 4.5" />
          <path d="M15.5 6.2c1.3.4 2.2 1.5 2.2 2.9 0 1.3-.8 2.4-2 2.8" />
          <path d="M17.5 14.7c1.8.6 3.1 2 3.8 4.3" />
        </svg>
      );
    case 'shield':
      return (
        <svg {...common}>
          <path d="M12 3.5l7 2.7v5.4c0 4.4-3 7.9-7 9-4-1.1-7-4.6-7-9V6.2l7-2.7z" strokeLinejoin="round" />
          <path d="M9 12l2 2 4-4" />
        </svg>
      );
    case 'shieldCheck':
      return (
        <svg {...common}>
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" strokeLinejoin="round" />
          <polyline points="9 12 11 14 15 10" />
        </svg>
      );
    case 'layers':
      return (
        <svg {...common}>
          <path d="M12 3l8 4.2-8 4.2-8-4.2L12 3z" strokeLinejoin="round" />
          <path d="M4 12.2L12 16l8-3.8M4 16.4L12 20l8-3.6" />
        </svg>
      );
    case 'wrench':
      return <svg {...common}><path d="M14.7 6.3a4 4 0 00-5.4 4.9L4 16.5V20h3.5l5.3-5.3a4 4 0 004.9-5.4l-2.6 2.6-2-2 2.6-2.6z" strokeLinejoin="round" /></svg>;
    case 'window':
      return <svg {...common}><rect x="4" y="4.5" width="16" height="15" rx="2" /><path d="M4 9.2h16M8 4.5v4.7" /></svg>;
    case 'plug':
      return <svg {...common}><path d="M9 3v4M15 3v4M6 7h12v3a6 6 0 01-12 0V7z" strokeLinejoin="round" /><path d="M12 16v5" /></svg>;
    case 'cloud':
      return <svg {...common}><path d="M7 18h10a3.5 3.5 0 000-7 5 5 0 00-9.6-1.6A4 4 0 007 18z" strokeLinejoin="round" /></svg>;
    case 'code':
      return <svg {...common}><path d="M9 8l-4 4 4 4M15 8l4 4-4 4" /></svg>;
    case 'check':
      return <svg {...common} strokeWidth={2.4}><path d="M5 12.5l4.5 4.5L19 7" /></svg>;
    case 'plus':
      return <svg {...common} strokeWidth={2}><path d="M12 5v14M5 12h14" /></svg>;
    case 'globe':
      return <svg {...common}><circle cx="12" cy="12" r="8" /><path d="M4 12h16M12 4c2.4 2.2 3.7 5 3.7 8s-1.3 5.8-3.7 8c-2.4-2.2-3.7-5-3.7-8s1.3-5.8 3.7-8z" /></svg>;
    case 'chip':
      return <svg {...common}><rect x="7" y="7" width="10" height="10" rx="1.5" /><path d="M9.5 3.5v2M14.5 3.5v2M9.5 18.5v2M14.5 18.5v2M3.5 9.5h2M3.5 14.5h2M18.5 9.5h2M18.5 14.5h2" /></svg>;
    case 'image':
      return <svg {...common}><rect x="4" y="5" width="16" height="14" rx="2" /><circle cx="9" cy="10" r="1.4" /><path d="M5 17l5-4.5 3.5 3L17 12l2 2.5" /></svg>;
    case 'lock':
      return <svg {...common}><rect x="5.5" y="10.5" width="13" height="9" rx="2" /><path d="M8 10.5V7.8a4 4 0 018 0v2.7" /></svg>;
    case 'file':
      return <svg {...common}><path d="M7 3.5h7l4 4V20a1 1 0 01-1 1H7a1 1 0 01-1-1V4.5a1 1 0 011-1z" strokeLinejoin="round" /><path d="M14 3.5V8h4" /></svg>;
    case 'message':
      return <svg {...common}><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" strokeLinejoin="round" /></svg>;
    case 'star':
      return <svg {...common} strokeLinejoin="round"><path d="M12 3l2.6 5.9 6.4.6-4.8 4.3 1.4 6.3L12 16.9 6.4 20.1l1.4-6.3-4.8-4.3 6.4-.6L12 3z" /></svg>;
    case 'target':
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="8.5" />
          <circle cx="12" cy="12" r="4.5" />
          <circle cx="12" cy="12" r="0.8" fill="currentColor" stroke="none" />
        </svg>
      );
    case 'bot':
      return (
        <svg {...common}>
          <rect x="5" y="9" width="14" height="10" rx="2.5" />
          <path d="M12 9V5M8.5 19v1.5M15.5 19v1.5" />
          <circle cx="12" cy="4" r="1.1" fill="currentColor" stroke="none" />
          <circle cx="9.2" cy="14" r="1.1" fill="currentColor" stroke="none" />
          <circle cx="14.8" cy="14" r="1.1" fill="currentColor" stroke="none" />
        </svg>
      );
    case 'brain':
      return (
        <svg {...common}>
          <path d="M9.5 4.2a3 3 0 00-3 3v.6a3 3 0 00-1.2 5.6A3 3 0 007 18.8a3 3 0 002.5 1.5" />
          <path d="M14.5 4.2a3 3 0 013 3v.6a3 3 0 011.2 5.6A3 3 0 0117 18.8a3 3 0 01-2.5 1.5" />
          <path d="M9.5 4.2v16M14.5 4.2v16" />
        </svg>
      );
    case 'lightbulb':
      return (
        <svg {...common}>
          <path d="M12 3a6 6 0 00-3.4 10.9c.6.42 1 1.1 1 1.85v.55h4.8v-.55c0-.75.4-1.43 1-1.85A6 6 0 0012 3z" strokeLinejoin="round" />
          <path d="M9.6 19h4.8M10.4 21.5h3.2" />
        </svg>
      );
    case 'workflow':
      return (
        <svg {...common}>
          <rect x="3" y="4" width="6.5" height="4.5" rx="1.2" />
          <rect x="14.5" y="4" width="6.5" height="4.5" rx="1.2" />
          <rect x="9" y="16" width="6.5" height="4.5" rx="1.2" />
          <path d="M6.2 8.5v3a2 2 0 002 2h1.3M17.8 8.5v3a2 2 0 01-2 2h-1.3" />
        </svg>
      );
    case 'search':
      return (
        <svg {...common}>
          <circle cx="10.5" cy="10.5" r="6.5" />
          <path d="M19.5 19.5L15.2 15.2" />
        </svg>
      );
    case 'compass':
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="8.5" />
          <path d="M15.3 8.7l-2.1 4.9-4.9 2.1 2.1-4.9 4.9-2.1z" fill="currentColor" stroke="none" />
        </svg>
      );
    case 'refresh':
      return (
        <svg {...common}>
          <path d="M20 11.5A8 8 0 106.3 16.8" />
          <polyline points="20 4.5 20 11.5 13 11.5" />
        </svg>
      );
    default:
      return null;
  }
}
